import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import TemplateRosterUpload from "./components/TemplateRosterUpload";
import Button from "../../components/Button";
import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
} from "@headlessui/react";

import { getPayingUsers } from "./api/athleteRequests";
import { useTranslation } from "../../i18n/TranslationProvider";

const AdminUpload = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState("");
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const { t } = useTranslation();

    useEffect(() => {
        const fetchPayingUsers = async () => {
            try {
                const { data } = await getPayingUsers();
                setUsers(data.users);
            } catch (err) {
                console.error("error fetching paying users", err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchPayingUsers();
    }, []);

    const filteredUsers = useMemo(() => {
        if (!query) return users;

        const q = query.toLowerCase();
        return users.filter((u) => {
            const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
            const email = u.email.toLowerCase();
            return fullName.includes(q) || email.includes(q);
        });
    }, [users, query]);

    if (loading) {
        return <h6 className="text-center text-lg font-normal">{t("common.loading")}</h6>;
    }
    return (
        <>
            <div className="custom-container mx-auto w-4/5 md:w-2/3 lg:w-1/2 ">
                <h4 className="text-lg md:text-2xl text-center mx-auto">
                    {t("athletes.adminUploadHeading")}
                </h4>
                <p className="text-center mb-6">
                    {t("athletes.adminUploadSubtext")}
                </p>
                <Combobox
                    value={selectedUser}
                    onChange={setSelectedUser}
                    onClose={() => setQuery("")}
                    immediate
                >
                    <div className="relative">
                        <ComboboxInput
                            onChange={(e) => setQuery(e.target.value)}
                            displayValue={(user) =>
                                user
                                    ? `${user.first_name} ${user.last_name} (${user.email})`
                                    : ""
                            }
                            placeholder={t("athletes.selectUserPlaceholder")}
                            className="w-full border rounded-lg border-primary px-2 py-2 bg-secondary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <ComboboxButton className="absolute inset-y-1 right-1 px-2.5">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                                className="size-4"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="m19.5 8.25-7.5 7.5-7.5-7.5"
                                />
                            </svg>
                        </ComboboxButton>
                        <ComboboxOptions className="cursor-pointer left-0 right-0 px-1 py-1 w-auto rounded-lg bg-secondary border-primary border absolute z-50 max-h-64 overflow-y-auto">
                            {filteredUsers.length > 0 ? (
                                filteredUsers.map((user) => (
                                    <ComboboxOption
                                        key={user.user_id}
                                        value={user}
                                        className={({ focus, selected }) =>
                                            `
                                            cursor-pointer select-none px-3 py-2 rounded-lg
                                            ${
                                                focus
                                                    ? "bg-indigo-100"
                                                    : "bg-red"
                                            }
                                            ${
                                                selected
                                                    ? "bg-indigo-500 text-white"
                                                    : "text-black"
                                            }`
                                        }
                                    >
                                        {user.first_name} {user.last_name} (
                                        {user.email})
                                    </ComboboxOption>
                                ))
                            ) : (
                                <ComboboxOption
                                    value={""}
                                    disabled
                                    className="cursor-default select-none px-3 py-2 text-gray-500 italic"
                                >
                                    {t("athletes.noUsersFound")}
                                </ComboboxOption>
                            )}
                        </ComboboxOptions>
                    </div>
                </Combobox>
                {selectedUser && (
                    <>
                        <h6 className="mt-6">{t("athletes.submittingForLabel")}</h6>
                        <h1 className="text-2xl mb-4">
                            {selectedUser.first_name} {selectedUser.last_name}{" "}
                            <span className="font-light">
                                ({selectedUser.email})
                            </span>
                        </h1>
                        <TemplateRosterUpload userId={selectedUser.user_id} />
                    </>
                )}
                <p className="text-red-600">{error}</p>
                <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => navigate("/")}
                >
                    {t("auth.back")}
                </Button>
                <div className="mt-4 px-2 flex justify-between items-center">
                    <p>{t("auth.needHelp")}</p>
                    <h6
                        className="text-teal-600 cursor-pointer"
                        onClick={() => navigate("/help")}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                navigate("/help");
                            }
                        }}
                    >
                        {t("auth.contactUs")}
                    </h6>
                </div>
            </div>
        </>
    );
};

export default AdminUpload;